/**
 * Ventas y destino de las porciones no vendidas (Req. 15).
 *
 * REGLA CENTRAL: el costo NO se recalcula, se TOMA de la produccion.
 *
 * La produccion congelo su costo el dia que se cocino. La venta se lleva la
 * parte que le corresponde por prorrateo, y las porciones que no se vendieron se
 * llevan el resto. Las dos partes suman exactamente el costo de la produccion,
 * porque el no vendido se obtiene por resta.
 *
 * Vender sin produccion registrada esta PERMITIDO: la usuaria ya cocinaba antes
 * de tener la aplicacion. En ese caso el costo queda en `null` y la venta se
 * marca como costo no disponible, en lugar de inventar un cero que mostraria una
 * ganancia falsa (Req. 15.10).
 *
 * Corregir una venta no la borra: se registra una venta nueva que apunta a la
 * anterior con `corrigeA`, y la anterior queda anulada (Req. 15.9). El historial
 * debe poder explicar cada cambio.
 */

import { db } from '../schema';
import { enEscritura } from '../tx';
import { nuevoId } from '../ids';
import {
  exigirCantidadPositiva,
  exigirCantidadNoNegativa,
  exigirImporteNoNegativo,
  textoOpcional,
} from '../validation';
import { ErrorValidacion } from '@/domain/errors';
import { ahoraIso, hoy, exigirFechaLocal, type FechaLocal } from '@/domain/dates';
import { prorratearCosto } from '@/domain/profit';
import { estaDescartado } from '@/domain/planning';
import type { Centavos } from '@/domain/money';
import type {
  DestinoSobrante,
  PlannedItem,
  Production,
  Recipe,
  Sale,
  SaleDisposition,
} from '../types';

// ---------------------------------------------------------------------------
// Lo que se puede vender
// ---------------------------------------------------------------------------

export interface PendienteDeVenta {
  platillo: PlannedItem;
  nombreReceta: string;
  /** Porciones producidas, si hubo produccion. Si no, las planeadas. */
  porcionesDisponibles: number;
  produccion: Production | null;
  /** Costo congelado de la produccion. null = no hay produccion. */
  costoProduccionCentavos: Centavos | null;
  costoEstimado: boolean;
  costoIncompleto: boolean;
  /** Porciones ya registradas como vendidas. */
  yaVendidas: number;
  /** Porciones ya asignadas a un destino. */
  yaAsignadas: number;
  /** Lo que queda por registrar. */
  porRegistrar: number;
  /** true si ya no queda nada por registrar. */
  cerrado: boolean;
}

/**
 * Platillos de una fecha listos para registrar su venta.
 *
 * Se ancla en la fecha de VENTA, no de preparacion: la venta ocurre el dia que
 * se entrega.
 */
export async function pendientesDeVenta(
  fecha: FechaLocal = hoy(),
): Promise<PendienteDeVenta[]> {
  const platillos = await db.plannedItems.where('fechaVenta').equals(fecha).toArray();
  return describirPendientes(platillos);
}

export async function pendientesEntre(
  desde: FechaLocal,
  hasta: FechaLocal,
): Promise<PendienteDeVenta[]> {
  const platillos = await db.plannedItems
    .where('fechaVenta')
    .between(desde, hasta, true, true)
    .toArray();
  return describirPendientes(platillos);
}

/**
 * Convierte platillos del menu en renglones por vender.
 *
 * LOS DESCARTADOS NO SE OFRECEN
 *
 * Un platillo marcado como "no se cocino, no hubo pedidos" no tiene nada que
 * vender, y aparecia igual pidiendo que se registraran sus porciones estimadas.
 * Es la misma molestia que ya se quito del tablero con la tinga: una tarea que la
 * usuaria no puede completar y que no se calla.
 *
 * Se filtra aqui y no en cada consulta para que valga tanto para el dia como para
 * el rango.
 */
async function describirPendientes(
  todos: readonly PlannedItem[],
): Promise<PendienteDeVenta[]> {
  const platillos = todos.filter((p) => !estaDescartado(p));
  if (platillos.length === 0) return [];

  const recetas = new Map(
    (await db.recipes.bulkGet([...new Set(platillos.map((p) => p.recipeId))]))
      .filter((r): r is Recipe => r !== undefined)
      .map((r) => [r.id, r]),
  );

  const producciones = await db.productions
    .where('plannedItemId')
    .anyOf(platillos.map((p) => p.id))
    .toArray();

  const ventas = await db.sales
    .where('plannedItemId')
    .anyOf(platillos.map((p) => p.id))
    .toArray();

  const destinos = await db.saleDispositions
    .where('plannedItemId')
    .anyOf(platillos.map((p) => p.id))
    .toArray();

  return platillos
    .map((platillo): PendienteDeVenta => {
      const produccion =
        producciones.find((p) => p.plannedItemId === platillo.id && p.estado === 'completada') ??
        null;

      // Las ventas anuladas por una correccion no cuentan.
      const anuladas = new Set(ventas.map((v) => v.corrigeA).filter((x) => x !== null));
      const vigentes = ventas.filter((v) => v.plannedItemId === platillo.id && !anuladas.has(v.id));

      const yaVendidas = vigentes.reduce((s, v) => s + v.porcionesVendidas, 0);
      const yaAsignadas = destinos
        .filter((d) => d.plannedItemId === platillo.id)
        .reduce((s, d) => s + d.porciones, 0);

      const porcionesDisponibles =
        produccion !== null ? produccion.porcionesAProducir : platillo.porcionesPlaneadas;

      const registradas = yaVendidas + yaAsignadas;

      return {
        platillo,
        nombreReceta: recetas.get(platillo.recipeId)?.nombre ?? '(receta eliminada)',
        porcionesDisponibles,
        produccion,
        costoProduccionCentavos: produccion?.costoTotalCentavos ?? null,
        costoEstimado: produccion?.costoEstimado ?? false,
        costoIncompleto: produccion?.costoIncompleto ?? false,
        yaVendidas,
        yaAsignadas,
        porRegistrar: Math.max(0, porcionesDisponibles - registradas),
        cerrado: registradas >= porcionesDisponibles,
      };
    })
    .sort((a, b) => a.nombreReceta.localeCompare(b.nombreReceta, 'es'));
}

// ---------------------------------------------------------------------------
// Registrar una venta
// ---------------------------------------------------------------------------

export interface RepartoSobrante {
  tipo: DestinoSobrante;
  porciones: number;
  notas?: string | null;
}

export interface DatosVenta {
  plannedItemId: string;
  porcionesVendidas: number;
  /** Si se omite, se usa el precio congelado del platillo del menu. */
  precioUnitarioCentavos?: Centavos;
  fecha?: FechaLocal;
  /** Destino de las porciones que no se vendieron (Req. 15.4, 15.5). */
  sobrantes?: readonly RepartoSobrante[];
  notas?: string | null;
  /** Id de la venta que esta corrige. */
  corrigeA?: string | null;
}

export interface ResultadoVenta {
  venta: Sale;
  destinos: SaleDisposition[];
  ingresoCentavos: Centavos;
  costoVendidoCentavos: Centavos | null;
  costoNoVendidoCentavos: Centavos | null;
  advertencias: string[];
}

/**
 * Registra cuantas porciones se vendieron y a donde fueron las demas.
 *
 * Escribe la venta y sus destinos en UNA transaccion: una venta sin el destino
 * de sus sobrantes dejaria el costo del lote sin cuadrar.
 */
export async function registrarVenta(datos: DatosVenta): Promise<ResultadoVenta> {
  const platillo = await db.plannedItems.get(datos.plannedItemId);
  if (platillo === undefined) {
    throw new ErrorValidacion('No se encontro el platillo del menu.');
  }

  // Cero es valido: puede que nada se vendiera y todo se fuera a consumo
  // personal o a merma. Ese caso tambien hay que poder registrarlo.
  const porcionesVendidas = exigirCantidadNoNegativa(
    datos.porcionesVendidas,
    'porciones vendidas',
  );
  const precioUnitarioCentavos = exigirImporteNoNegativo(
    datos.precioUnitarioCentavos ?? platillo.precioVentaCentavos,
    'precio',
  );
  const fecha = exigirFechaLocal(datos.fecha ?? platillo.fechaVenta, 'fecha');
  const advertencias: string[] = [];

  const sobrantes = (datos.sobrantes ?? []).filter((s) => s.porciones > 0);
  for (const sobrante of sobrantes) {
    exigirCantidadPositiva(sobrante.porciones, 'porciones del destino');
  }

  // --- Cuantas porciones hay en total ---------------------------------
  const produccion =
    (await db.productions.where('plannedItemId').equals(platillo.id).toArray()).find(
      (p) => p.estado === 'completada',
    ) ?? null;

  const porcionesProducidas =
    produccion !== null ? produccion.porcionesAProducir : platillo.porcionesPlaneadas;

  const porcionesSobrantes = sobrantes.reduce((s, x) => s + x.porciones, 0);
  const cubreAhora = porcionesVendidas + porcionesSobrantes;

  /**
   * LO YA REGISTRADO ANTES DE ESTA CAPTURA.
   *
   * Una venta puede capturarse en varias veces: se vendieron 2 en la manana y 2
   * en la tarde. Sin contar lo anterior, cada captura repartiria el costo como si
   * fuera la unica, y el costo total asignado al lote saldria mayor que el costo
   * real de la produccion. Eso descuadraria las ganancias.
   */
  const previas = await db.sales.where('plannedItemId').equals(platillo.id).toArray();
  const anuladas = new Set(previas.map((v) => v.corrigeA).filter((x) => x !== null));
  const vigentes = previas.filter((v) => !anuladas.has(v.id));
  const destinosPrevios = await db.saleDispositions
    .where('plannedItemId')
    .equals(platillo.id)
    .toArray();

  const yaVendidas = vigentes.reduce((s, v) => s + v.porcionesVendidas, 0);
  const yaAsignadas = destinosPrevios.reduce((s, d) => s + d.porciones, 0);
  const yaCubiertas = yaVendidas + yaAsignadas;

  const yaCostoAsignado =
    vigentes.reduce((s, v) => s + (v.costoCentavos ?? 0), 0) +
    destinosPrevios.reduce((s, d) => s + (d.costoCentavos ?? 0), 0);

  const totalRegistrado = yaCubiertas + cubreAhora;

  if (totalRegistrado > porcionesProducidas) {
    advertencias.push(
      `Estas registrando ${totalRegistrado} porciones en total y solo hay ` +
        `${porcionesProducidas}. Revisa las cantidades.`,
    );
  }

  if (totalRegistrado < porcionesProducidas) {
    advertencias.push(
      `Quedan ${porcionesProducidas - totalRegistrado} porciones sin registrar. ` +
        'Puedes decir a donde fueron para que el costo cuadre.',
    );
  }

  // --- Prorrateo del costo congelado ----------------------------------
  const costoProduccion = produccion?.costoTotalCentavos ?? null;

  let costoVendidoCentavos: Centavos | null = null;
  let costoNoVendidoCentavos: Centavos | null = null;

  if (costoProduccion === null) {
    advertencias.push(
      'No hay una produccion registrada para este platillo, asi que el costo de la venta ' +
        'queda como no disponible. El ingreso si se registra.',
    );
  } else if (cubreAhora === 0) {
    costoVendidoCentavos = 0;
    costoNoVendidoCentavos = 0;
  } else {
    /**
     * Costo que le toca a ESTA captura.
     *
     * La captura que CIERRA el lote se lleva el remanente exacto, en lugar de su
     * proporcion: asi la suma de todas las capturas es igual al costo de la
     * produccion, centavo a centavo, sin que el redondeo deje sobras.
     */
    const cierraElLote = totalRegistrado >= porcionesProducidas;

    const costoDeEstaCaptura = cierraElLote
      ? costoProduccion - yaCostoAsignado
      : Math.round((costoProduccion * cubreAhora) / porcionesProducidas);

    // Dentro de la captura, vendido y no vendido se parten por resta.
    const reparto = prorratearCosto(costoDeEstaCaptura, porcionesVendidas, cubreAhora);
    costoVendidoCentavos = reparto.vendidoCentavos;
    costoNoVendidoCentavos = reparto.noVendidoCentavos;
  }

  // --- Correccion -----------------------------------------------------
  const corrigeA = datos.corrigeA ?? null;
  if (corrigeA !== null) {
    const anterior = await db.sales.get(corrigeA);
    if (anterior === undefined) {
      throw new ErrorValidacion('No se encontro la venta que se quiere corregir.');
    }
  }

  const saleId = nuevoId('venta');

  const venta: Sale = {
    id: saleId,
    fecha,
    plannedItemId: platillo.id,
    recipeId: platillo.recipeId,
    productionId: produccion?.id ?? null,
    porcionesVendidas,
    precioUnitarioCentavos,
    ingresoCentavos: Math.round(porcionesVendidas * precioUnitarioCentavos),
    costoCentavos: costoVendidoCentavos,
    corrigeA,
    notas: textoOpcional(datos.notas, 'notas', 200),
    creadoEn: ahoraIso(),
  };

  // El costo no vendido se reparte entre los destinos, en proporcion a sus
  // porciones y por resta, para que la suma cierre exacto contra la produccion.
  const destinos: SaleDisposition[] = [];
  let costoRepartido = 0;

  sobrantes.forEach((sobrante, indice) => {
    const esUltimo = indice === sobrantes.length - 1;

    let costo: Centavos | null = null;
    if (costoNoVendidoCentavos !== null) {
      costo = esUltimo
        ? costoNoVendidoCentavos - costoRepartido
        : Math.round((costoNoVendidoCentavos * sobrante.porciones) / porcionesSobrantes);
      costoRepartido += costo;
    }

    destinos.push({
      id: nuevoId('dest'),
      plannedItemId: platillo.id,
      productionId: produccion?.id ?? null,
      saleId,
      tipo: sobrante.tipo,
      porciones: sobrante.porciones,
      costoCentavos: costo,
      notas: textoOpcional(sobrante.notas, 'notas', 200),
      creadoEn: ahoraIso(),
    });
  });

  await enEscritura([db.sales, db.saleDispositions], async () => {
    await db.sales.add(venta);
    if (destinos.length > 0) await db.saleDispositions.bulkAdd(destinos);
  });

  return {
    venta,
    destinos,
    ingresoCentavos: venta.ingresoCentavos,
    costoVendidoCentavos,
    costoNoVendidoCentavos,
    advertencias,
  };
}

/**
 * Destinos que pertenecen a una venta concreta.
 *
 * Los registros con `saleId` se atribuyen a su venta. Los que no lo traen son
 * anteriores a que existiera el campo y solo se pueden atribuir por lote; en ese
 * caso se devuelven unicamente si la venta es la unica del platillo, para no
 * arrastrar los sobrantes de otra captura.
 */
async function destinosDeVenta(venta: Sale): Promise<SaleDisposition[]> {
  if (venta.plannedItemId === null) return [];

  const delPlatillo = await db.saleDispositions
    .where('plannedItemId')
    .equals(venta.plannedItemId)
    .toArray();

  const propios = delPlatillo.filter((d) => d.saleId === venta.id);
  const sinDueno = delPlatillo.filter((d) => d.saleId === undefined);

  if (sinDueno.length === 0) return propios;

  const ventasDelPlatillo = await db.sales
    .where('plannedItemId')
    .equals(venta.plannedItemId)
    .toArray();

  return ventasDelPlatillo.length === 1 ? [...propios, ...sinDueno] : propios;
}

/**
 * Corrige una venta conservando el rastro de la correccion (Req. 15.9).
 *
 * No modifica la original: registra una nueva que la anula y la reemplaza. Se
 * retiran SOLO los destinos de la venta corregida, no los de otras capturas del
 * mismo platillo.
 */
export async function corregirVenta(
  saleId: string,
  datos: Omit<DatosVenta, 'plannedItemId' | 'corrigeA'>,
): Promise<ResultadoVenta> {
  const anterior = await db.sales.get(saleId);
  if (anterior === undefined) {
    throw new ErrorValidacion('No se encontro la venta.');
  }
  if (anterior.plannedItemId === null) {
    throw new ErrorValidacion('Esta venta no esta asociada a un platillo del menu.');
  }

  const yaCorregida = (await db.sales.toArray()).find((v) => v.corrigeA === saleId);
  if (yaCorregida !== undefined) {
    throw new ErrorValidacion('Esta venta ya se corrigio.');
  }

  const viejos = await destinosDeVenta(anterior);
  await db.saleDispositions.bulkDelete(viejos.map((d) => d.id));

  return registrarVenta({
    ...datos,
    plannedItemId: anterior.plannedItemId,
    corrigeA: saleId,
  });
}

// ---------------------------------------------------------------------------
// Editar y eliminar
// ---------------------------------------------------------------------------

export interface CambiosVenta {
  porcionesVendidas?: number;
  precioUnitarioCentavos?: Centavos;
  fecha?: FechaLocal;
  sobrantes?: readonly RepartoSobrante[];
  notas?: string | null;
}

/**
 * Edita una venta en su lugar.
 *
 * SE BORRA Y SE VUELVE A REGISTRAR, en una sola transaccion. No es un atajo: el
 * prorrateo del costo se calcula contando lo que ya esta registrado del lote, asi
 * que retirar primero la version vieja hace que el recalculo salga correcto sin
 * duplicar ni perder centavos. Modificar los numeros en su lugar exigiria
 * reimplementar el prorrateo, y dos implementaciones acabarian discrepando.
 *
 * EL INVENTARIO NO SE TOCA, porque una venta nunca lo toco: los insumos se
 * descuentan al completar la produccion. El costo congelado de la produccion
 * tampoco cambia; lo unico que se recalcula es la parte de ese costo que le
 * corresponde a esta venta.
 */
export async function editarVenta(
  saleId: string,
  cambios: CambiosVenta,
): Promise<ResultadoVenta> {
  const anterior = await db.sales.get(saleId);
  if (anterior === undefined) {
    throw new ErrorValidacion('No se encontro la venta.');
  }
  if (anterior.plannedItemId === null) {
    throw new ErrorValidacion(
      'Esta venta no esta asociada a un platillo del menu, asi que no se puede editar.',
    );
  }

  const corregidaPor = (await db.sales.toArray()).find((v) => v.corrigeA === saleId);
  if (corregidaPor !== undefined) {
    throw new ErrorValidacion(
      'Esta venta ya fue reemplazada por una correccion. Edita la correccion.',
    );
  }

  const destinosViejos = await destinosDeVenta(anterior);

  // Lo que no se indique conserva su valor anterior.
  const sobrantes =
    cambios.sobrantes ??
    destinosViejos.map((d) => ({ tipo: d.tipo, porciones: d.porciones, notas: d.notas }));

  await enEscritura([db.sales, db.saleDispositions], async () => {
    await db.saleDispositions.bulkDelete(destinosViejos.map((d) => d.id));
    await db.sales.delete(saleId);
  });

  return registrarVenta({
    plannedItemId: anterior.plannedItemId,
    porcionesVendidas: cambios.porcionesVendidas ?? anterior.porcionesVendidas,
    precioUnitarioCentavos:
      cambios.precioUnitarioCentavos ?? anterior.precioUnitarioCentavos,
    fecha: cambios.fecha ?? anterior.fecha,
    sobrantes,
    notas: cambios.notas === undefined ? anterior.notas : cambios.notas,
    corrigeA: anterior.corrigeA,
  });
}

/**
 * Elimina una venta y sus destinos.
 *
 * Al retirarla, sus porciones y su parte del costo vuelven a quedar disponibles
 * para registrarse de nuevo, y el lote regresa al estado en que estaba antes de
 * la captura.
 *
 * NO devuelve insumos al inventario, porque la venta nunca los saco. Para deshacer
 * el consumo hay que revertir la produccion.
 *
 * Si esta venta corregia a otra, la corregida vuelve a quedar vigente: se anulo
 * por existir la correccion, y al desaparecer la correccion ya no hay razon para
 * mantenerla anulada.
 */
export async function eliminarVenta(saleId: string): Promise<void> {
  const venta = await db.sales.get(saleId);
  if (venta === undefined) {
    throw new ErrorValidacion('No se encontro la venta.');
  }

  const corregidaPor = (await db.sales.toArray()).find((v) => v.corrigeA === saleId);
  if (corregidaPor !== undefined) {
    throw new ErrorValidacion(
      'Esta venta ya fue reemplazada por una correccion. Elimina la correccion en su lugar.',
    );
  }

  const destinos = await destinosDeVenta(venta);

  await enEscritura([db.sales, db.saleDispositions], async () => {
    await db.saleDispositions.bulkDelete(destinos.map((d) => d.id));
    await db.sales.delete(saleId);
  });
}

// ---------------------------------------------------------------------------
// Consultas
// ---------------------------------------------------------------------------

/** Ventas vigentes de un rango: excluye las anuladas por una correccion. */
export async function ventasEntre(desde: FechaLocal, hasta: FechaLocal): Promise<Sale[]> {
  const todas = await db.sales.where('fecha').between(desde, hasta, true, true).toArray();
  const anuladas = new Set(
    (await db.sales.toArray()).map((v) => v.corrigeA).filter((x) => x !== null),
  );
  return todas.filter((v) => !anuladas.has(v.id));
}

export async function destinosEntre(
  desde: FechaLocal,
  hasta: FechaLocal,
): Promise<Array<SaleDisposition & { fecha: FechaLocal }>> {
  // saleDispositions no guarda fecha propia: la toma del platillo del menu.
  const destinos = await db.saleDispositions.toArray();
  if (destinos.length === 0) return [];

  const platillos = new Map(
    (await db.plannedItems.bulkGet([...new Set(destinos.map((d) => d.plannedItemId))]))
      .filter((p): p is PlannedItem => p !== undefined)
      .map((p) => [p.id, p]),
  );

  return destinos
    .map((destino) => ({
      ...destino,
      fecha: platillos.get(destino.plannedItemId)?.fechaVenta ?? '',
    }))
    .filter((d) => d.fecha >= desde && d.fecha <= hasta);
}

export async function obtenerVenta(id: string): Promise<Sale | undefined> {
  return db.sales.get(id);
}

export async function destinosDePlatillo(plannedItemId: string): Promise<SaleDisposition[]> {
  return db.saleDispositions.where('plannedItemId').equals(plannedItemId).toArray();
}
